/**
 * Storage Operations (VISTOERA_EXTERNAL_INFRA_OPERATIONS_V1) — the
 * dedicated "存储运维" entry point spec §二 requires: not just another
 * "817MB" number, but a real breakdown of what that number is made of
 * (§四/§五), an orphan/garbage audit that tells SAFE_TO_DELETE apart from
 * IN_USE (§七), a real (never-fake) cleanup Dry Run (§十/§十一), and the
 * Render Backend/Database detail this round audited (§十五-§十九).
 *
 * Every section renders exactly what the backend actually returned —
 * `available: false` / `Not available from provider` is shown honestly,
 * never silently coerced into a 0 or hidden.
 */
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  FolderOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  StopOutlined,
} from '@ant-design/icons'
import { Alert, Button, Table, Tabs, Tag, Tooltip, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  platformGetDeepzoomStorage,
  platformGetRenderBackendDetail,
  platformGetRenderDatabaseDetail,
  platformGetStorageLifecycle,
  platformGetStorageOrphanAudit,
  platformGetStorageOverview,
  platformRunCleanupDryRun,
  platformRunStorageAuditScan,
} from '../api'
import type {
  ExternalServiceHealth,
  RenderProviderDetail,
  StorageCleanupDryRun,
  StorageDeepzoomSummary,
  StorageLifecycleSnapshot,
  StorageOrphanAudit,
  StorageOrphanFinding,
  StorageUsageBreakdown,
} from '../types'
import { SectionCard, SummaryCard, formatBytes, formatDateTime, formatRelativeTime } from './adminShared'
import { cardIconBg, platformColors } from './theme'

const HEALTH_TONE: Record<ExternalServiceHealth, { bg: string; color: string; label: string; icon: React.ReactNode }> = {
  healthy: { bg: platformColors.successBg, color: platformColors.success, label: 'Healthy', icon: <CheckCircleOutlined /> },
  warning: { bg: platformColors.warningBg, color: platformColors.warning, label: 'Warning', icon: <ExclamationCircleOutlined /> },
  critical: { bg: platformColors.dangerBg, color: platformColors.danger, label: 'Critical', icon: <StopOutlined /> },
  unknown: { bg: '#F1F5F9', color: platformColors.secondaryText, label: 'Unknown', icon: <QuestionCircleOutlined /> },
  not_configured: { bg: '#F1F5F9', color: platformColors.mutedText, label: 'Not configured', icon: <QuestionCircleOutlined /> },
}

function HealthBadge({ health }: { health: ExternalServiceHealth }) {
  const tone = HEALTH_TONE[health] ?? HEALTH_TONE.unknown
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        fontWeight: 600,
        padding: '1px 8px',
        borderRadius: 4,
        background: tone.bg,
        color: tone.color,
        whiteSpace: 'nowrap',
      }}
    >
      {tone.icon} {tone.label}
    </span>
  )
}

const SOURCE_LABEL: Record<string, string> = {
  provider_api: 'Provider API 实时同步',
  deployment_config: 'Deployment 配置（非实时 API，来自部署文档记录）',
  environment_metadata: 'Environment 探测（进程自身环境变量）',
  manual_registry: 'Manual 登记（人工填写，非 Provider API）',
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? '是' : '否'
  if (typeof value === 'number') return value.toLocaleString('zh-CN')
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function KeyValueList({ data, emptyText = '暂无数据' }: { data: Record<string, unknown> | undefined; emptyText?: string }) {
  const entries = Object.entries(data ?? {}).filter(([k]) => !k.startsWith('_'))
  if (entries.length === 0) {
    return <div style={{ fontSize: 12, color: platformColors.mutedText, padding: '4px 8px' }}>{emptyText}</div>
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 12px', padding: '4px 8px' }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'contents' }}>
          <div style={{ fontSize: 12, color: platformColors.secondaryText }}>{k}</div>
          <div style={{ fontSize: 12, color: platformColors.primaryText, wordBreak: 'break-word' }}>{renderValue(v)}</div>
        </div>
      ))}
    </div>
  )
}

const CATEGORY_LABEL: Record<string, string> = {
  original: 'Original images 原图',
  thumbnail_preview: 'Thumbnail / Preview 缩略图',
  transparent: 'Transparent 透明抠图',
  background: 'Background variants 背景变体',
  shadow: 'Shadow variants 阴影变体',
  deepzoom: 'DeepZoom tiles 深度缩放瓦片',
  video: 'Video outputs 视频输出',
  publish_artifacts: 'Publish artifacts 发布快照',
  system: 'System 系统对象',
  other: 'Other 未识别',
}

const ORPHAN_TYPE_LABEL: Record<string, string> = {
  orphan_deleted_asset: 'DB 无对应 Asset 记录（Asset 已删除）',
  deleted_tenant_prefix: '租户/工作区已删除，前缀遗留',
  deepzoom_orphan_tiles: 'DeepZoom 瓦片无对应 Ready Capability',
  stale_derivative: '无匹配 AssetVariant 记录的旧变体',
  replaced_derivative: '已被后续编辑替换的旧派生文件',
  upload_interrupted: '上传中断遗留的临时对象',
  failed_task_leftover: '失败任务遗留输出',
  published_snapshot_reference: '仍被已发布 Site 快照引用（受保护）',
  not_audited_this_round: '本轮未纳入审计范围（Publish/System/未识别）',
  unknown: '未知形态',
}

const CLASSIFICATION_TONE: Record<string, { bg: string; color: string; label: string }> = {
  SAFE_TO_DELETE: { bg: platformColors.dangerBg, color: platformColors.danger, label: 'SAFE_TO_DELETE' },
  REVIEW_REQUIRED: { bg: platformColors.warningBg, color: platformColors.warning, label: 'REVIEW_REQUIRED' },
  IN_USE: { bg: platformColors.successBg, color: platformColors.success, label: 'IN_USE' },
  UNKNOWN: { bg: '#F1F5F9', color: platformColors.secondaryText, label: 'UNKNOWN' },
}

function ClassificationTag({ classification }: { classification: string }) {
  const tone = CLASSIFICATION_TONE[classification] ?? CLASSIFICATION_TONE.UNKNOWN
  return (
    <Tag style={{ background: tone.bg, color: tone.color, border: 'none', fontSize: 11, fontWeight: 600 }}>{tone.label}</Tag>
  )
}

function NotAvailable({ reason }: { reason?: string | null }) {
  return (
    <div style={{ padding: '10px 8px', fontSize: 12, color: platformColors.mutedText }}>
      Not available from provider{reason ? `：${reason}` : ''}
    </div>
  )
}

// --------------------------------------------------------------- R2 Tab

function R2StorageTab() {
  const [overview, setOverview] = useState<StorageUsageBreakdown | null>(null)
  const [deepzoom, setDeepzoom] = useState<StorageDeepzoomSummary | null>(null)
  const [lifecycle, setLifecycle] = useState<StorageLifecycleSnapshot | null>(null)
  const [audit, setAudit] = useState<StorageOrphanAudit | null>(null)
  const [dryRun, setDryRun] = useState<StorageCleanupDryRun | null>(null)
  const [loading, setLoading] = useState(true)
  const [analyzing, setAnalyzing] = useState(false)
  const [dryRunning, setDryRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadAll = useCallback((opts?: { force?: boolean }) => {
    setLoading(true)
    Promise.all([
      platformGetStorageOverview(opts?.force),
      platformGetDeepzoomStorage(),
      platformGetStorageLifecycle(),
      platformGetStorageOrphanAudit(opts?.force),
    ])
      .then(([ov, dz, lc, au]) => {
        setOverview(ov.data)
        setDeepzoom(dz.data)
        setLifecycle(lc.data)
        setAudit(au.data)
        setError(null)
      })
      .catch((err) => setError(err.message || '数据加载失败'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => loadAll(), [loadAll])

  const runAnalyze = () => {
    setAnalyzing(true)
    platformRunStorageAuditScan()
      .then((res) => {
        setAudit(res.data)
        message.success('深度审计已完成，并已记录到 Platform Audit Log')
      })
      .catch((err) => message.error(err.message || '审计失败'))
      .finally(() => setAnalyzing(false))
  }

  const runDryRun = () => {
    setDryRunning(true)
    platformRunCleanupDryRun()
      .then((res) => {
        setDryRun(res.data)
        message.success('Dry Run 完成（未删除任何对象），已记录到 Platform Audit Log')
      })
      .catch((err) => message.error(err.message || 'Dry Run 失败'))
      .finally(() => setDryRunning(false))
  }

  const showSkeleton = loading && !overview
  const breakdownEntries = Object.entries(overview?.breakdown ?? {})
  const orphanTypeEntries = Object.entries(audit?.by_orphan_type ?? {})

  return (
    <div>
      {error && (
        <Alert type="error" showIcon message={`数据加载失败：${error}`} style={{ marginBottom: 10 }} />
      )}

      {/* Spec §一/§三 — usage-based, never a fabricated fixed capacity. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <SummaryCard
          title="Storage Used 当前使用量"
          loading={showSkeleton}
          value={overview?.available ? formatBytes(overview.used_bytes ?? null) : '暂不可用'}
          hint={overview?.available ? `对象数 ${overview.object_count?.toLocaleString('zh-CN') ?? '—'}` : overview?.reason ?? undefined}
          iconBg={cardIconBg.storage}
          icon={<CloudServerOutlined style={{ color: platformColors.info }} />}
        />
        <SummaryCard
          title="Capacity Model 容量模型"
          value="Usage-based（无固定总容量）"
          hint="Cloudflare R2 当前套餐按用量计费，不存在如 10GB/1TB 的固定总容量上限 — 不制造该数字"
          iconBg={cardIconBg.storage}
          icon={<FolderOutlined style={{ color: platformColors.secondaryText }} />}
        />
        <SummaryCard
          title="Class A / B Requests · Egress · Estimated Cost"
          value="Not available from provider"
          hint="需要 Cloudflare Account 级只读 Token（Account.Read+Analytics/Billing.Read），当前未配置 — 见外部服务详情 MANUAL_ACTION_REQUIRED"
          iconBg={cardIconBg.storage}
          icon={<QuestionCircleOutlined style={{ color: platformColors.mutedText }} />}
        />
        <SummaryCard
          title="DeepZoom Storage"
          loading={showSkeleton}
          value={deepzoom?.available ? formatBytes(deepzoom.total_bytes ?? null) : '暂不可用'}
          hint={
            deepzoom?.available
              ? `${deepzoom.deepzoom_asset_count ?? 0} 个资产 · ${deepzoom.tile_object_count ?? 0} 个瓦片对象 · 均值 ${formatBytes(deepzoom.average_bytes_per_asset ?? null)}/资产`
              : deepzoom?.reason ?? undefined
          }
          iconBg={cardIconBg.storage}
          icon={<FolderOutlined style={{ color: platformColors.info }} />}
        />
      </div>

      <SectionCard
        title="Usage Breakdown 数据分布（按类型）"
        extra={
          <Tooltip title="按 tenant/site/asset type/derivative type 统计，来自 DB Asset metadata + Storage key 命名规范，不依赖文件名猜测">
            <span style={{ cursor: 'help', color: platformColors.mutedText, fontSize: 11 }}>数据来源 ⓘ</span>
          </Tooltip>
        }
      >
        {overview?.available ? (
          <Table
            className="pf-data-table"
            sticky
            size="small"
            rowKey="category"
            pagination={false}
            loading={showSkeleton}
            dataSource={breakdownEntries.map(([category, stat]) => ({ category, ...stat }))}
            locale={{ emptyText: '暂无对象' }}
            columns={[
              { title: '类型', dataIndex: 'category', render: (v: string) => CATEGORY_LABEL[v] ?? v },
              { title: '对象数', dataIndex: 'object_count', render: (v: number) => v.toLocaleString('zh-CN') },
              { title: '大小', dataIndex: 'bytes', render: (v: number) => formatBytes(v) },
              {
                title: '占比',
                key: 'pct',
                render: (_: unknown, row: { bytes: number }) =>
                  overview.used_bytes ? `${((row.bytes / overview.used_bytes) * 100).toFixed(1)}%` : '—',
              },
            ]}
          />
        ) : (
          <NotAvailable reason={overview?.reason} />
        )}
      </SectionCard>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
        <div style={{ flex: '1 1 320px', minWidth: 320 }}>
          <SectionCard title="Top Tenants by Storage">
            <Table
              className="pf-data-table"
              sticky
              size="small"
              rowKey={(r) => r.workspace_id}
              pagination={false}
              dataSource={overview?.top_tenants ?? []}
              locale={{ emptyText: '暂无数据' }}
              columns={[
                { title: 'Workspace ID', dataIndex: 'workspace_id', render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</span> },
                { title: '对象数', dataIndex: 'object_count' },
                { title: '大小', dataIndex: 'bytes', render: (v: number) => formatBytes(v) },
              ]}
            />
          </SectionCard>
        </div>
        <div style={{ flex: '1 1 320px', minWidth: 320 }}>
          <SectionCard title="Top Assets by Storage">
            <Table
              className="pf-data-table"
              sticky
              size="small"
              rowKey={(r) => `${r.subsystem}:${r.asset_id}`}
              pagination={false}
              dataSource={overview?.top_assets ?? []}
              locale={{ emptyText: '暂无数据' }}
              columns={[
                { title: 'Asset ID', dataIndex: 'asset_id', render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</span> },
                { title: '子系统', dataIndex: 'subsystem' },
                { title: '大小', dataIndex: 'bytes', render: (v: number) => formatBytes(v) },
              ]}
            />
          </SectionCard>
        </div>
        <div style={{ flex: '1 1 320px', minWidth: 320 }}>
          <SectionCard title="Top Individual Files">
            <Table
              className="pf-data-table"
              sticky
              size="small"
              rowKey={(r) => r.storage_key}
              pagination={false}
              dataSource={overview?.top_files ?? []}
              locale={{ emptyText: '暂无数据' }}
              columns={[
                { title: 'Storage Key（脱敏）', dataIndex: 'storage_key', render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{v}</span> },
                { title: '大小', dataIndex: 'bytes', render: (v: number) => formatBytes(v) },
              ]}
            />
          </SectionCard>
        </div>
      </div>

      <SectionCard
        title="Cleanup Candidates — Orphan / Garbage Audit"
        extra={
          <Button size="small" icon={<ReloadOutlined spin={analyzing} />} onClick={runAnalyze} loading={analyzing}>
            Analyze（深度审计）
          </Button>
        }
      >
        {audit?.available ? (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '6px 8px' }}>
              <SummaryCard
                title="Safe to Delete"
                value={audit.safe_to_delete_count ?? 0}
                hint={formatBytes(audit.safe_to_delete_bytes ?? null)}
                iconBg={cardIconBg.storage}
                icon={<DeleteOutlined style={{ color: platformColors.danger }} />}
              />
              <SummaryCard
                title="Review Required"
                value={audit.review_required_count ?? 0}
                hint={formatBytes(audit.review_required_bytes ?? null)}
                iconBg={cardIconBg.storage}
                icon={<ExclamationCircleOutlined style={{ color: platformColors.warning }} />}
              />
              <SummaryCard
                title="Published Snapshot 保护"
                value={audit.published_snapshot_protected_count ?? 0}
                hint="仍被已发布 Site 快照引用，禁止清理"
                iconBg={cardIconBg.storage}
                icon={<SafetyCertificateOutlined style={{ color: platformColors.success }} />}
              />
            </div>

            <div style={{ padding: '4px 8px', fontSize: 12, color: platformColors.secondaryText }}>
              按原因归类（共扫描 {audit.scanned_object_count?.toLocaleString('zh-CN') ?? '—'} 个对象 · 更新于 {formatRelativeTime(audit.updated_at)}）：
            </div>
            <Table
              className="pf-data-table"
              sticky
              size="small"
              rowKey="orphan_type"
              pagination={false}
              dataSource={orphanTypeEntries.map(([orphan_type, stat]) => ({ orphan_type, ...stat }))}
              locale={{ emptyText: '暂无发现' }}
              columns={[
                { title: '原因', dataIndex: 'orphan_type', render: (v: string) => ORPHAN_TYPE_LABEL[v] ?? v },
                { title: '对象数', dataIndex: 'object_count' },
                { title: '大小', dataIndex: 'bytes', render: (v: number) => formatBytes(v) },
              ]}
            />

            <div style={{ padding: '10px 8px 4px', fontSize: 13, fontWeight: 600, color: platformColors.primaryText }}>样本明细（按大小排序，最多 25 条）</div>
            <Table
              className="pf-data-table"
              sticky
              size="small"
              rowKey={(r) => r.storage_key}
              pagination={false}
              dataSource={audit.sample_findings ?? []}
              locale={{ emptyText: '暂无样本' }}
              scroll={{ x: true }}
              columns={[
                { title: 'Storage Key（脱敏）', dataIndex: 'storage_key', render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>{v}</span> },
                { title: '类型', dataIndex: 'category', render: (v: string) => CATEGORY_LABEL[v] ?? v },
                { title: '大小', dataIndex: 'bytes', render: (v: number) => formatBytes(v) },
                { title: '分类', dataIndex: 'classification', render: (v: StorageOrphanFinding['classification']) => <ClassificationTag classification={v} /> },
                { title: '原因', dataIndex: 'reason' },
              ]}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 8px 4px' }}>
              <Button type="primary" danger icon={<DeleteOutlined />} loading={dryRunning} onClick={runDryRun}>
                Dry Run Cleanup（预演，不会真正删除）
              </Button>
              <Tooltip title="真正执行删除需要更高权限层级（当前仅有 require_platform_admin 单一门槛）以及正式的 soft-delete/Asset Contract 删除管道，本轮尚未构建 — 明确禁用，而非一个能点但什么都不做的假按钮">
                <Button disabled icon={<StopOutlined />}>
                  Execute Cleanup（本轮不可用）
                </Button>
              </Tooltip>
            </div>

            {dryRun && (
              <div
                style={{
                  margin: '4px 8px 8px',
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: platformColors.dangerBg,
                  border: `1px solid ${platformColors.danger}`,
                  fontSize: 12,
                  color: platformColors.primaryText,
                }}
              >
                <div style={{ fontWeight: 600, color: platformColors.danger, marginBottom: 4 }}>Dry Run 结果（未删除任何对象）</div>
                <div>预计删除对象数：{dryRun.would_delete_object_count?.toLocaleString('zh-CN') ?? 0}</div>
                <div>预计释放空间：{formatBytes(dryRun.would_reclaim_bytes ?? null)}</div>
                <div>受影响的 active assets：{dryRun.affected_active_assets_count ?? 0}</div>
                <div>受影响的 published snapshots：{dryRun.affected_published_snapshots_count ?? 0}</div>
                <div>计算时间：{formatDateTime(dryRun.computed_at)}</div>
              </div>
            )}
          </>
        ) : (
          <NotAvailable reason={audit?.reason} />
        )}
      </SectionCard>

      <SectionCard title="R2 Lifecycle Rules（只读）">
        {lifecycle?.available ? (
          lifecycle.configured ? (
            <Table
              className="pf-data-table"
              sticky
              size="small"
              rowKey={(r, i) => r.id ?? String(i)}
              pagination={false}
              dataSource={lifecycle.rules}
              columns={[
                { title: 'Rule ID', dataIndex: 'id', render: (v: string | null) => v ?? '—' },
                { title: '状态', dataIndex: 'status', render: (v: string | null) => v ?? '—' },
                { title: 'Prefix', dataIndex: 'prefix', render: (v: string | null) => v ?? '（全部）' },
                { title: '过期天数', dataIndex: 'expiration_days', render: (v: number | null) => (v != null ? `${v} 天` : '—') },
              ]}
            />
          ) : (
            <div style={{ padding: '10px 8px', fontSize: 12, color: platformColors.warning }}>
              Lifecycle not configured — 建议为临时/孤儿对象前缀配置过期规则。
            </div>
          )
        ) : (
          <NotAvailable reason={lifecycle?.reason} />
        )}
      </SectionCard>
    </div>
  )
}

// ----------------------------------------------------------- Render Tab

function RenderProviderSection({ title, icon, detail, loading }: { title: string; icon: React.ReactNode; detail: RenderProviderDetail | null; loading: boolean }) {
  return (
    <SectionCard
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {icon}
          <span>{title}</span>
          {detail && <HealthBadge health={detail.health} />}
        </div>
      }
    >
      {loading && !detail ? (
        <div style={{ padding: '10px 8px', fontSize: 12, color: platformColors.mutedText }}>加载中…</div>
      ) : detail ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ padding: '4px 8px', fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>用量 Usage</div>
            <KeyValueList data={detail.usage} emptyText="Provider 未返回可用用量数据" />
          </div>
          <div style={{ padding: '4px 8px', fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>配额 Quota</div>
            <KeyValueList data={detail.quota} emptyText="无固定配额上限，或 Provider 未提供" />
          </div>
          <div style={{ padding: '4px 8px', fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>计费 / 续期</div>
            <div style={{ fontSize: 12, color: platformColors.primaryText }}>
              {detail.billing ? (
                <>
                  模式：{detail.billing.billing_mode} · 预估月费：{detail.billing.estimated_monthly_charge ?? '—'}
                  {detail.billing.plan == null && !detail.billing.estimated_monthly_charge && ' （尚未人工登记）'}
                </>
              ) : (
                '尚未人工登记 Monthly Cost（Source=Manual，Render API 不提供账单数据）'
              )}
            </div>
            <div style={{ fontSize: 12, color: platformColors.primaryText, marginTop: 2 }}>
              {detail.renewal?.next_billing_date
                ? `下次账单：${formatDateTime(detail.renewal.next_billing_date)}`
                : detail.renewal?.note ?? '—'}
            </div>
          </div>
          <div style={{ padding: '4px 8px', fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>详细信息 Metadata</div>
            <KeyValueList data={detail.metadata} />
          </div>
          {detail.error && <div style={{ padding: '4px 8px', fontSize: 12, color: platformColors.danger }}>最近一次同步错误：{detail.error}</div>}
          <div style={{ padding: '4px 8px', fontSize: 11, color: platformColors.mutedText }}>
            数据来源：{SOURCE_LABEL[detail.source] ?? detail.source} · 最近同步：{formatRelativeTime(detail.last_sync)}
          </div>
        </div>
      ) : (
        <NotAvailable />
      )}
    </SectionCard>
  )
}

function RenderInfrastructureTab() {
  const [backend, setBackend] = useState<RenderProviderDetail | null>(null)
  const [database, setDatabase] = useState<RenderProviderDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    Promise.all([platformGetRenderBackendDetail(), platformGetRenderDatabaseDetail()])
      .then(([b, d]) => {
        setBackend(b.data)
        setDatabase(d.data)
        setError(null)
      })
      .catch((err) => setError(err.message || '数据加载失败'))
      .finally(() => setLoading(false))
  }, [])

  const region = (backend?.metadata?.region as string | undefined) ?? null
  const regionSourceNote = (backend?.metadata?.region_source as string | undefined) ?? null

  return (
    <div>
      {error && <Alert type="error" showIcon message={`数据加载失败：${error}`} style={{ marginBottom: 10 }} />}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 4 }}>
        <SummaryCard
          title="Region"
          value={region ?? 'Not configured'}
          hint={regionSourceNote ?? (backend ? '当前环境未记录已确认的部署 Region' : undefined)}
          iconBg={cardIconBg.worker}
          icon={<CloudServerOutlined style={{ color: platformColors.info }} />}
        />
        <SummaryCard
          title="Backend Service"
          value={(backend?.metadata?.service_name as string | undefined) ?? (backend?.metadata?.render_service_name as string | undefined) ?? '—'}
          hint={backend?.metadata?.plan ? `Plan: ${backend.metadata.plan as string}` : (backend?.metadata?.manual_action_required as string | undefined)}
          iconBg={cardIconBg.worker}
          icon={<CloudServerOutlined style={{ color: platformColors.secondaryText }} />}
        />
        <SummaryCard
          title="Deploy SHA"
          value={((backend?.metadata?.render_git_commit as string | undefined) ?? (backend?.metadata?.latest_deploy_commit as string | undefined) ?? '—').toString().slice(0, 12)}
          hint={backend?.metadata?.latest_deploy_status ? `最近部署状态：${backend.metadata.latest_deploy_status as string}` : undefined}
          iconBg={cardIconBg.worker}
          icon={<ClockCircleOutlined style={{ color: platformColors.mutedText }} />}
        />
        <SummaryCard
          title="Database Size"
          value={database?.usage?.database_size_bytes != null ? formatBytes(database.usage.database_size_bytes as number) : '—'}
          hint={database?.metadata?.engine_version ? `${database.metadata.engine as string} ${database.metadata.engine_version as string}` : undefined}
          iconBg={cardIconBg.storage}
          icon={<DatabaseOutlined style={{ color: platformColors.secondaryText }} />}
        />
      </div>

      <RenderProviderSection title="Render Backend（Web Service）" icon={<CloudServerOutlined style={{ color: platformColors.info }} />} detail={backend} loading={loading} />
      <RenderProviderSection title="Render Database（PostgreSQL）" icon={<DatabaseOutlined style={{ color: platformColors.secondaryText }} />} detail={database} loading={loading} />
    </div>
  )
}

// --------------------------------------------------------------- Page

export default function StorageOperationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') === 'render' ? 'render' : 'r2'

  return (
    <div style={{ maxWidth: 1600 }}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 20, lineHeight: '26px', fontWeight: 600, color: platformColors.primaryText }}>存储运维 Storage Operations</div>
        <div style={{ fontSize: 12, lineHeight: '16px', color: platformColors.secondaryText, marginTop: 2 }}>
          Cloudflare R2 真实用量分布 / Top Consumers / 孤儿对象审计 / Cleanup Dry Run，以及 Render Backend + Database
          的真实资源规格 —— 无法从 Provider 获取的数据一律诚实标注，从不伪造
        </div>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setSearchParams(key === 'r2' ? {} : { tab: key })}
        items={[
          { key: 'r2', label: 'Cloudflare R2', children: <R2StorageTab /> },
          { key: 'render', label: 'Render Infrastructure', children: <RenderInfrastructureTab /> },
        ]}
      />
    </div>
  )
}
